import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';

const Wishlists = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: 'My Wishlist',
  });

  const fetchWishlists = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/wishlists/?family_id=${currentFamily.id}`);
      setWishlists(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchWishlists();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchWishlists]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    
    // Validate that currentFamily has a valid ID
    if (!currentFamily.id) {
      alert('Invalid family selected. Please refresh the page or select a different family.');
      return;
    }
    
    try {
      const response = await api.post('/wishlists/', {
        ...formData,
        family_id: currentFamily.id,
      });
      setWishlists([response.data, ...wishlists]);
      setOpen(false);
      setFormData({ title: 'My Wishlist' });
      navigate(`/wishlists/${response.data.id}`);
    } catch (error) {
      
      // If the error is about family not found, refresh families
      if (error.response?.data?.error && error.response.data.error.includes('not a member of family')) {
        alert('The selected family is no longer available. Refreshing family list...');
        // Trigger a refresh of families
        window.location.reload();
      } else {
        alert(error.response?.data?.error || 'Failed to create wishlist');
      }
    }
  };

  const copyShareLink = (shareToken) => {
    const url = `${window.location.origin}/wishlists/shared/${shareToken}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gift Wishlists</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Create Wishlist
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start creating wishlists.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Typography>Loading...</Typography>
      ) : wishlists.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No wishlists yet. Create your first wishlist!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {wishlists.map((wishlist) => {
            // Get first item with image for preview
            const previewItem = wishlist.items?.find(item => item.image_url);
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={wishlist.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => navigate(`/wishlists/${wishlist.id}`)}
                >
                  {previewItem?.image_url ? (
                    <Box
                      component="img"
                      src={previewItem.image_url}
                      alt={wishlist.title}
                      sx={{
                        width: '100%',
                        height: { xs: 180, sm: 200 },
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 180, sm: 200 },
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CardGiftcardIcon sx={{ fontSize: { xs: 50, sm: 60 }, color: 'grey.400' }} />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Typography variant="h6" gutterBottom noWrap>
                          {wishlist.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {wishlist.items?.length || 0} items
                        </Typography>
                      </Box>
                      <Tooltip title="Copy share link">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            copyShareLink(wishlist.share_token);
                          }}
                          size="small"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/wishlists/${wishlist.id}`);
                      }}
                    >
                      View
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Wishlist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Wishlist Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Wishlists;
